import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { CardBrandIcon } from '@/components/ui';
import {
  useWithdrawalCancel,
  useWithdrawalList,
  getWithdrawalGetBalanceQueryKey,
  getWithdrawalListQueryKey,
} from '@/lib/endpoints';
import { WithdrawalControllerListStatus } from '@/lib/models/withdrawalControllerListStatus';
import { formatCurrency } from '@/lib/format-currency';
import { SkrillLogo } from './SkrillLogo';

/** Rows visible before the list scrolls (per the V2 design's card height). */
const VISIBLE_ROWS = 3;

/**
 * Pending withdrawal requests as the V2 card: a header with the request
 * count and total, and scrollable pill rows — method brand mark, amount,
 * and a cancel button. Renders nothing while empty.
 */
export function PendingWithdrawalsList() {
  const queryClient = useQueryClient();

  const query = useWithdrawalList({ status: WithdrawalControllerListStatus.PENDING });

  const cancelMutation = useWithdrawalCancel({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getWithdrawalGetBalanceQueryKey() });
        queryClient.invalidateQueries({ queryKey: getWithdrawalListQueryKey() });
      },
    },
  });

  if (query.isLoading) {
    return <div className="text-sm text-ink-muted">Loading pending withdrawals…</div>;
  }
  if (query.isError) {
    return <div className="text-sm text-danger-alt">Could not load pending withdrawals.</div>;
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return null;
  }

  const totalCents = rows.reduce((sum, row) => sum + row.amount, 0);
  const scrollable = rows.length > VISIBLE_ROWS;

  return (
    <section className="rounded-[12px] border border-stroke-soft bg-surface p-3.5 shadow-card">
      <div className="flex items-center justify-between px-1 pb-2">
        <h2 className="text-[18px] font-semibold text-ink">Pending Requests ({rows.length})</h2>
        <span className="text-[16px] text-ink">
          Total <span className="font-semibold">{formatCurrency(totalCents, rows[0].currency)}</span>
        </span>
      </div>
      <div className="relative">
        <ul className="flex max-h-[190px] flex-col gap-1 overflow-y-auto">
          {rows.map((row) => {
            const isCancellingThis = cancelMutation.isPending && cancelMutation.variables?.id === row.id;
            const errorOnThis = Boolean(cancelMutation.error) && cancelMutation.variables?.id === row.id;
            return (
              <li
                key={row.id}
                className="flex shrink-0 items-center justify-between rounded-[33px] border-2 border-input-soft bg-surface py-2.5 pl-4 pr-3"
              >
                <div className="flex items-center gap-2.5">
                  <MethodBadge methodCode={row.methodCode} />
                  <span className="text-[20px] text-ink tabular-nums">{formatCurrency(row.amount, row.currency)}</span>
                  {errorOnThis && <span className="text-xs text-danger-alt">{extractError(cancelMutation.error)}</span>}
                </div>
                <button
                  type="button"
                  disabled={isCancellingThis}
                  onClick={() => cancelMutation.mutate({ id: row.id })}
                  className="h-8 rounded-pill border-[1.5px] border-danger/70 bg-danger/10 px-4 text-[15px] font-semibold capitalize text-danger-alt transition-opacity disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 focus-visible:ring-offset-2"
                >
                  {isCancellingThis ? 'Cancelling…' : 'Cancel'}
                </button>
              </li>
            );
          })}
        </ul>
        {scrollable && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent"
          />
        )}
      </div>
    </section>
  );
}

/** Brand mark for a withdrawal's payout method, falling back to the raw code. */
function MethodBadge({ methodCode }: { methodCode?: string }) {
  const code = (methodCode ?? '').toLowerCase();
  if (code.includes('skrill')) return <SkrillLogo />;
  if (code.includes('bank')) return <Landmark className="size-7 text-purple" strokeWidth={1.75} aria-hidden="true" />;
  if (code.includes('card')) return <CardBrandIcon brand="unknown" size={36} />;
  return <span className="text-sm font-medium capitalize text-ink-muted">{methodCode ?? 'Unknown'}</span>;
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string | string[] })?.message;
    const reason = Array.isArray(message) ? message[0] : message;
    if (reason) return reason;
  }
  return 'Could not cancel.';
}
